import React from 'react';
import styled from 'styled-components';
import { object } from 'yup';
import { useTranslation } from 'react-i18next';
import { string } from '../../../../../../common/src/constants/validationSchemas';

// Components
import { IconAtticFilled, IconActionClose, Title } from 'wg-fe-ui';
import RadioInputComponent from '../../../ui/advisory-flow/components/shared-components/RadioInputComponent';

// Types
import { CellarAttic } from '../../../../../../../types/QuoteQuestion';
import { Address } from '../../../../../../../types/Common';
import AssistantQuestionSkeleton from '../../../../../../sales-conversation/src/components/flow/AssistantQuestionSkeleton';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import QuestionGroupActions from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import { Button, Flex, VStack } from '@wegroup/design-system';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';
import { formatAddress } from '../../../../../../common/src/utils/commonUtils';

interface Props {
  address?: Address;
  atticVal?: CellarAttic;
  cellarVal?: CellarAttic;
  previousSubView: () => void;
  nextQuestionHandler: ({
    attic,
    cellar,
  }: {
    attic?: string;
    cellar?: string;
  }) => void;
  handleNewSearch: () => void;
}

type Input = {
  attic: string;
  cellar: string;
};

interface BuildingOption {
  name: string;
  value: CellarAttic;
  icon: React.ReactElement;
  description?: string;
}

const HomeQuestions: React.FC<React.PropsWithChildren<Props>> = ({
  address,
  atticVal,
  cellarVal,
  nextQuestionHandler,
  previousSubView,
  handleNewSearch,
}) => {
  const { t } = useTranslation();

  const atticOptions = [
    { name: t('None'), value: 'NONE', icon: <IconActionClose /> },
    {
      name: t('Inhabitable attic'),
      value: 'INHABITABLE',
      description: t(
        'The building has an attic which can be used as a living space',
      ),
      icon: <IconAtticFilled />,
    },
    {
      name: t('Uninhabitable attic'),
      value: 'UNINHABITABLE',
      description: t(
        'The building has an attic which can not be used as a living space',
      ),
      icon: <IconAtticFilled />,
    },
  ] as BuildingOption[];

  const cellarOptions = [
    { name: t('None'), value: 'NONE', icon: <IconActionClose /> },
    {
      name: t('Inhabitable cellar'),
      value: 'INHABITABLE',
      description: t(
        'The building has a cellar which can be used as a living space',
      ),
      icon: <IconAtticFilled />,
    },
    {
      name: t('Uninhabitable cellar'),
      value: 'UNINHABITABLE',
      description: t(
        'The building has a cellar which can not be used as a living space',
      ),
      icon: <IconAtticFilled />,
    },
  ] as BuildingOption[];

  const validationSchema = object().shape({
    attic: string.required,
    cellar: string.required,
  });

  const {
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<Input>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      attic: atticVal,
      cellar: cellarVal,
    },
  });

  const Question = () =>
    address ? (
      <QuestionGroupAssistantQuestion
        question={t(`Let’s get to know`, {
          address: formatAddress(address),
        })}
        horizontal
      />
    ) : (
      <AssistantQuestionSkeleton />
    );
  return (
    <AdvisoryFlowMainContainer data-test-id="ProdFac_HomeQuestions">
      <Question />
      <Flex
        p={{ base: '4', laptop: '8' }}
        pt={{ base: '2', laptop: '4' }}
        alignItems="flex-start"
        justifyContent="center"
        flexGrow={1}
      >
        <div>
          <Flex justify={{ base: 'flex-start', tablet: 'center' }} mb="6">
            <Button onClick={handleNewSearch} variant="link">
              {t('FLOWS_INPUT_HOME-INPUT_NEW-SEARCH')}
            </Button>
          </Flex>
          <VStack
            spacing="2"
            alignItems="stretch"
            data-test-id="ProdFac_HomeQuestions_Attic"
            mb="6"
          >
            <Title>{t('Attic')}</Title>
            {errors?.attic && <Error>{errors.attic.message}</Error>}
            {atticOptions?.map(({ name, description, value, icon }, key) => {
              return (
                <StyledRadioInputComponent
                  key={key}
                  Icon={icon}
                  title={name}
                  description={description}
                  onClick={() => setValue('attic', value)}
                  isSelected={watch('attic') === value}
                />
              );
            })}
          </VStack>
          <VStack
            spacing="2"
            alignItems="stretch"
            data-test-id="ProdFac_HomeQuestions_Cellar"
          >
            <Title>{t('Cellar')}</Title>
            {errors?.cellar && <Error>{errors.cellar.message}</Error>}
            {cellarOptions?.map(({ name, description, value, icon }, key) => {
              return (
                <StyledRadioInputComponent
                  key={key}
                  Icon={icon}
                  title={name}
                  description={description}
                  onClick={() => setValue('cellar', value)}
                  isSelected={watch('cellar') === value}
                />
              );
            })}
          </VStack>
        </div>
      </Flex>
      <QuestionGroupActions
        onBack={previousSubView}
        onContinue={handleSubmit(nextQuestionHandler)}
      />
    </AdvisoryFlowMainContainer>
  );
};

const Error = styled.p`
  color: ${({ theme }) => theme.status.error};
  margin-bottom: 0.8rem;
`;

const StyledRadioInputComponent = styled(RadioInputComponent)`
  margin-bottom: 0.8rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export default HomeQuestions;
